import { z } from 'zod';

/**
 * One error envelope for the whole HTTP surface.
 *
 * `code` is machine-readable and stable; `message` is for a human reading a log or a
 * form error. Both halves matter: a UI that has to regex-match a message to decide
 * whether to show a confirm dialog is a UI that breaks when the message is reworded.
 *
 * `details` carries whatever the specific code needs - `fieldId` for a validation
 * failure, `confirmToken` plus the pre-flight summary for a refused destructive
 * change (INV-8), the zod issue list for a malformed body.
 */

export const API_ERROR_CODES = [
  'unauthenticated',
  'forbidden',
  'not_found',
  'invalid_request',
  'invalid_value',
  'invalid_state',
  'confirmation_required',
  'confirmation_mismatch',
  'preflight_drift',
  'not_supported',
  'rate_limited',
  'conflict',
  /**
   * accounts F1: a one-time link that EXISTED and is spent - an invitation that expired,
   * was accepted or was revoked, and a password-reset link that expired or was used.
   *
   * TWO CODES AND NOT ONE, because the two screens reading them are two screens: an
   * accept page never meets a reset link and vice versa, and a shared code would make
   * every message a lookup through `details.reason` plus a guess about which resource it
   * was about. A link that was never issued is `not_found` with the same `reason` shape,
   * because `API_ERROR_STATUS` maps one code to one status and 404 is what an unknown
   * token is (DEVIATIONS-ACCOUNTS.md, decision 3).
   */
  'invitation_invalid',
  'reset_invalid',
  'unavailable',
  'internal',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const apiErrorSchema = z.strictObject({
  error: z.strictObject({
    code: z.literal([...API_ERROR_CODES]),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** HTTP status per code. One table, so no route invents its own mapping. */
export const API_ERROR_STATUS: Readonly<Record<ApiErrorCode, number>> = {
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  invalid_request: 400,
  invalid_value: 400,
  invalid_state: 400,
  // 409, not 400: the request was understood and is legal, it just needs the caller
  // to confirm the plan it was shown.
  confirmation_required: 409,
  confirmation_mismatch: 409,
  /**
   * admin-area.md D6. 409 like its two neighbours and for the same reason: the request
   * was understood and is legal, but the world moved between the preview and the
   * confirmation, so the caller has to look again.
   */
  preflight_drift: 409,
  // 422: understood, well-formed, and refused - a field type change, a filter on a
  // relation kind with no column. Not the caller's syntax, but not our bug either.
  not_supported: 422,
  rate_limited: 429,
  conflict: 409,
  /**
   * 410 Gone, and deliberately not 404: the link was real, the person is holding a copy of
   * something this server issued, and "it existed and is finished" is the only answer that
   * lets the screen say "ask for a new one" instead of "check the address".
   */
  invitation_invalid: 410,
  reset_invalid: 410,
  unavailable: 503,
  internal: 500,
};
